import { useEffect } from "react";
import { createRoute, useNavigate, useSearch } from "@tanstack/react-router";

import { rootRoute } from "./root.tsx";
import { Spinner } from "../components/ui.tsx";
import { writeIdentity, writePurchaseAs, writeViewAs } from "../lib/identity.ts";

/**
 * ═══════════════════════════════════════════════════════════════
 *  /restock → 商城（进货模式）
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **「进货」不再是一个单独的页面，而是商城的一种价格视角。**
 *
 *    以前这是一张纯文字列表：没有图片、没有分类、没有搜索、
 *    还自带一个跟购物车并行的迷你选货框。老板的原话是
 *    「我根本看不到这些商品的图片什么的，非常影响我的用户体验」。
 *
 *    而同一时间，他在**首页**看到的是**平台指导零售价** ——
 *    给没有归属的陌生人看的最贵一档，他这辈子都不会按那个价买。
 *    于是首页对他毫无意义：「那我这个首页又有什么意义呢？」
 *
 *    现在：切到商家身份，**整个商城就按他的进货价显示** ——
 *    图片、分类、搜索、购物车、结算全都是现成的。
 *
 * ⚠️ 这个路由保留做**重定向**：商家端菜单、旧书签还指着它。
 */
function RestockRedirect() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/restock" }) as { orgId?: number };

  useEffect(() => {
    /**
     * ⚠️ 把视角一起切过去 —— 只跳到商城却不切的话，他点「进货」看到的还是
     *    **客户价**（如果他刚才正在看自己的店），等于这个入口坏了。
     *
     * 🔴 进货 = 「我自己」这个视角：身份清空（不是某家店）、按进货价看。
     *    店铺视角是"客户看到的价"，只能看不能买。
     */
    if (search.orgId) {
      writePurchaseAs(search.orgId);
    }
    writeIdentity("");
    writeViewAs("purchase");
    /** ⚠️ `replace` —— 不留历史，否则按返回会弹回来又被重定向走，出不去。 */
    navigate({ to: "/products", search: {} as never, replace: true });
  }, [navigate, search.orgId]);

  /*
    ⚠️ 这是个纯重定向页，别套 `TabPage` —— 那是手机版的外壳（含底部标签栏），
       在电脑上会闪一下 480 宽的手机界面再跳走。
  */
  return (
    <div className="flex min-h-dvh items-center justify-center">
      <Spinner label="正在打开进货" compact />
    </div>
  );
}

export const restockRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/restock",
  component: RestockRedirect,
  validateSearch: (s: Record<string, unknown>): { orgId?: number } => ({
    orgId: s.orgId ? Number(s.orgId) : undefined,
  }),
});
